import { useState } from "react";
import { RefreshCw, ShieldAlert, Home, TrendingUp, AlertTriangle, DollarSign, Building2, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import { fmtCurrencyFull } from "@/lib/format";
import useUiCopy from "@/hooks/useUiCopy";
import { useAuth } from "@/context/AuthContext";


const _fmtInt = (v) => (v === null || v === undefined || v === "" ? ", " : Number(v).toLocaleString());
const _fmtCurr = (v) => (v === null || v === undefined || v === "" ? ", " : fmtCurrencyFull(v));
const _fmtDate = (v) => (!v ? ", " : fmtDateTime(v));
const _fmtPct = (v) => (v === null || v === undefined || v === "" ? ", " : `${Number(v).toFixed(1)}%`);


function MetricCard({ label, value, sub, tone = "slate", testid, tooltip }) {
  const toneCls = {
    slate: "text-slate-100",
    emerald: "text-emerald-300",
    orange: "text-orange-300",
    rose: "text-rose-300",
  }[tone] || "text-slate-100";
  return (
    <div className="ae-card p-4" data-testid={testid} title={tooltip || undefined}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono text-2xl tabular-nums mt-1 ${toneCls}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}


export default function AttomIntelligencePanel({ opp, onUpdate }) {
  const copy = useUiCopy();
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin", "manager"].includes(user?.role);
  const [busy, setBusy] = useState(false);
  if (!opp) return null;

  const asset = (opp.asset_type || "").toLowerCase();
  if (asset === "business" || (asset !== "multifamily" && asset !== "land")) return null;

  const status = opp.attom_data_status || {};
  const hasData = Object.values(status).some((v) => v === true) || opp.attom_avm_value != null;

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/opportunities/${opp.oid}/attom/refresh?force=true`);
      const opps = r.data.opp || {};
      toast.success("Property data refresh complete");
      onUpdate?.(opps);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Property data refresh failed");
    } finally {
      setBusy(false);
    }
  };

  const avm = opp.attom_avm_value;
  const eqPct = opp.attom_equity_pct_calculated;
  const eqAmt = opp.attom_equity_calculated;
  const conf = opp.attom_avm_confidence_score;
  const confTone = conf >= 80 ? "emerald" : conf >= 60 ? "orange" : "slate";
  const eqTone = eqPct >= 60 ? "emerald" : eqPct >= 30 ? "orange" : "slate";

  const sales = Array.isArray(opp.attom_sale_history) ? opp.attom_sale_history.slice(0, 5) : [];
  const foreclosureTone = opp.attom_foreclosure_flag ? "rose" : "slate";
  const taxTone = opp.attom_tax_delinquent_attom ? "rose" : "slate";

  return (
    <div className="ae-card p-5 space-y-5" data-testid="attom-intelligence-panel">
      {/* Fallback banner (BatchData standing in for ATTOM) */}
      {opp.property_data_source === "BatchData Fallback" && (
        <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-3 flex items-start gap-2" data-testid="attom-fallback-banner">
          <div className="flex-1 text-xs">
            <div className="text-amber-300 font-semibold">{copy.property_panel_fallback}</div>
            <div className="text-amber-200/80 mt-0.5">Neighborhood analytics and hazard scores not available in fallback mode.</div>
          </div>
          {isAdmin && (
            <a href="/app/settings" className="text-xs text-amber-300 hover:text-amber-200 underline whitespace-nowrap" data-testid="attom-enable-in-settings-link">
              Enable ATTOM in Settings →
            </a>
          )}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
          <span>{copy.property_panel_title}</span>
          <span title={copy.property_panel_tooltip} className="cursor-help text-slate-600 hover:text-emerald-300 transition-colors">
            <Info className="h-3 w-3" />
          </span>
        </div>
        <button
          onClick={refresh}
          disabled={busy}
          data-testid="attom-refresh-btn"
          className="text-xs inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-white/10 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-300 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Fetching…" : hasData ? "Re-enrich" : "Enrich Now"}
        </button>
      </div>

      {!hasData ? (
        <div className="text-sm text-slate-500 py-6 text-center" data-testid="attom-no-data">
          {copy.property_panel_empty}
        </div>
      ) : (
        <>
          {/* Metric cards row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              label={copy.property_panel_avm_label}
              value={_fmtCurr(avm)}
              sub={avm ? `Range ${_fmtCurr(opp.attom_avm_value_low)}-${_fmtCurr(opp.attom_avm_value_high)}${conf ? ` · Conf ${Math.round(conf)}` : ""}` : ", "}
              tone={confTone}
              testid="attom-avm-card"
            />
            <MetricCard
              label="Est. Equity"
              value={eqPct != null ? _fmtPct(eqPct) : ", "}
              sub={eqAmt != null ? _fmtCurr(eqAmt) : "AVM − Mortgage"}
              tone={eqTone}
              testid="attom-equity-card"
            />
            <MetricCard
              label="Annual Tax"
              value={_fmtCurr(opp.attom_tax_amount_annual)}
              sub={opp.attom_tax_year ? `Tax year ${opp.attom_tax_year}` : ", "}
              tone={taxTone}
              testid="attom-tax-card"
            />
            <MetricCard
              label="Open Liens"
              value={_fmtInt(opp.attom_lien_count)}
              sub={opp.attom_lien_total_amount ? `Total ${_fmtCurr(opp.attom_lien_total_amount)}` : (opp.attom_lien_count > 0 ? "amount unknown" : "clean")}
              tone={opp.attom_lien_count > 0 ? "rose" : "slate"}
              testid="attom-liens-card"
            />
          </div>

          {/* Property Details + Risk Signals */}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="ae-card p-4" data-testid="attom-property-details">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                <Home className="h-3 w-3" /> Property Details
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                <KV label="Year built" value={_fmtInt(opp.attom_year_built)} />
                <KV label="Building sqft" value={_fmtInt(opp.attom_sqft_building)} />
                <KV label="Lot sqft" value={_fmtInt(opp.attom_lot_size_sqft)} />
                <KV label="Units" value={_fmtInt(opp.attom_units_count_attom)} />
                <KV label="Zoning" value={opp.attom_zoning || ", "} />
                <KV label="Type" value={opp.attom_property_type || ", "} />
                <KV label="Years owned" value={opp.attom_years_owned_calculated ? `${opp.attom_years_owned_calculated} yr` : ", "} />
                <KV label="Lender" value={opp.attom_lender_name || ", "} />
                <KV label="Mortgage type" value={opp.attom_mortgage_type || ", "} />
                <KV label="Last sale" value={opp.attom_last_sale_date ? `${_fmtDate(opp.attom_last_sale_date)} · ${_fmtCurr(opp.attom_last_sale_price)}` : ", "} />
                <KV label="Prior sale" value={opp.attom_prior_sale_date ? `${_fmtDate(opp.attom_prior_sale_date)} · ${_fmtCurr(opp.attom_prior_sale_price)}` : ", "} />
              </div>
            </div>

            <div className="ae-card p-4" data-testid="attom-risk-signals">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                <ShieldAlert className="h-3 w-3" /> Risk Signals
              </div>
              <div className="space-y-2">
                <div className={`px-3 py-2 rounded-sm border flex items-center justify-between ${opp.attom_foreclosure_flag ? "text-rose-300 border-rose-500/40 bg-rose-500/10" : "text-emerald-300 border-emerald-500/30 bg-emerald-500/5"}`}>
                  <span className="text-xs uppercase tracking-wider">Foreclosure</span>
                  <span className="font-mono text-xs">
                    {opp.attom_foreclosure_flag
                      ? `Yes${opp.attom_foreclosure_stage ? ` · ${opp.attom_foreclosure_stage}` : ""}${opp.attom_foreclosure_filing_date ? ` · ${_fmtDate(opp.attom_foreclosure_filing_date)}` : ""}`
                      : "No"}
                  </span>
                </div>
                <div className={`px-3 py-2 rounded-sm border flex items-center justify-between ${opp.attom_tax_delinquent_attom ? "text-rose-300 border-rose-500/40 bg-rose-500/10" : "text-emerald-300 border-emerald-500/30 bg-emerald-500/5"}`}>
                  <span className="text-xs uppercase tracking-wider">Tax Delinquent</span>
                  <span className="font-mono text-xs">{opp.attom_tax_delinquent_attom ? "Yes" : "No"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Neighborhood & hazard sections removed in Iter 45 ,  endpoints
             /neighborhood/community and /hazard/homeowner/withrisk are not
             on the current ATTOM plan tier. See "Higher tier" footer below. */}

          {/* Sales history */}
          {sales.length > 0 && (
            <div className="ae-card p-4" data-testid="attom-sales-history">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3" /> Sales History (last {sales.length})
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs">
                {sales.map((s, i) => (
                  <div key={i} className="border border-white/8 rounded-sm px-3 py-2">
                    <div className="text-slate-500 text-[10px] uppercase tracking-wider">{_fmtDate(s.date)}</div>
                    <div className="font-mono text-slate-100">{_fmtCurr(s.price)}</div>
                    {s.price && opp.attom_sqft_building > 0 && (
                      <div className="text-slate-500 text-[10px]">${Math.round(s.price / opp.attom_sqft_building)}/sqft</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data status row (which calls succeeded) */}
          {Object.keys(status).length > 0 && (
            <div className="text-[10px] text-slate-600 flex flex-wrap gap-2" data-testid="attom-data-status">
              {Object.entries(status).map(([k, v]) => (
                <span key={k} className={`px-1.5 py-0.5 rounded-sm border ${v ? "border-emerald-500/25 text-emerald-400" : "border-slate-700 text-slate-500"}`}>
                  {k}: {v ? "ok" : "n/a"}
                </span>
              ))}
              {opp.attom_last_refresh && (
                <span className="ml-auto text-slate-600">refreshed {fmtDateTime(opp.attom_last_refresh)}</span>
              )}
            </div>
          )}

          {/* Plan-tier disclosure ,  surfaces what's gated behind higher tiers. */}
          <div className="text-[11px] text-slate-500 italic pt-1 border-t border-white/5" data-testid="attom-tier-footer">
            Additional neighborhood and sales history intelligence available on higher plan tiers.
          </div>
        </>
      )}
    </div>
  );
}


function KV({ label, value, sub }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-slate-200 font-mono tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}
